import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const ManualTimeEntry = ({ onSave, onCancel, editingEntry, defaultRate }) => {
  const [caseId, setCaseId] = useState('');
  const [caseName, setCaseName] = useState('');
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(30);
  const [hourlyRate, setHourlyRate] = useState(defaultRate || 0);
  const [billable, setBillable] = useState(true);
  const [activityDescription, setActivityDescription] = useState('');
  const [startDate, setStartDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingEntry) {
      setCaseId(editingEntry?.case_id || '');
      setCaseName(editingEntry?.case_name || '');
      const totalMins = editingEntry?.duration_minutes || 0;
      setHours(Math.floor(totalMins / 60));
      setMinutes(totalMins % 60);
      setHourlyRate(editingEntry?.hourly_rate || defaultRate || 0);
      setBillable(editingEntry?.billable_flag !== false);
      setActivityDescription(editingEntry?.activity_description || '');
      if (editingEntry?.started_at) {
        const d = new Date(editingEntry.started_at);
        setStartDate(d?.toISOString()?.split('T')?.[0]);
        setStartTime(d?.toTimeString()?.slice(0, 5));
      }
    } else {
      setHourlyRate(defaultRate || 0);
    }
  }, [editingEntry, defaultRate]);

  const validate = () => {
    const newErrors = {};
    if (!activityDescription?.trim()) newErrors.activityDescription = 'Activity description is required';
    if (hours === 0 && minutes === 0) newErrors.duration = 'Duration must be greater than 0';
    if (!hourlyRate || Number(hourlyRate) < 0) newErrors.hourlyRate = 'Valid hourly rate is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    const durationMinutes = (Number(hours) * 60) + Number(minutes);
    const startedAt = new Date(`${startDate}T${startTime}:00`);
    const endedAt = new Date(startedAt.getTime() + durationMinutes * 60000);
    onSave({
      caseId,
      caseName,
      durationMinutes,
      hourlyRate: Number(hourlyRate),
      billable,
      activityDescription,
      startedAt: startedAt?.toISOString(),
      endedAt: endedAt?.toISOString()
    });
  };

  const billableAmount = ((Number(hours) + Number(minutes) / 60) * Number(hourlyRate))?.toFixed(2);

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading-semibold text-text-primary">
          {editingEntry ? 'Edit Time Entry' : 'New Manual Entry'}
        </h3>
        {editingEntry && (
          <span className="text-xs bg-warning-50 text-warning-700 border border-warning-200 px-2 py-1 rounded-full">
            Editing Draft
          </span>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Duration */}
        <div>
          <label className="block text-sm font-body-medium text-text-secondary mb-1.5">
            Duration <span className="text-error-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={hours}
                onChange={e => setHours(Math.max(0, parseInt(e?.target?.value) || 0))}
                min="0"
                max="23"
                className="w-20 px-3 py-2.5 border border-border rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-secondary">hrs</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={minutes}
                onChange={e => setMinutes(Math.min(59, Math.max(0, parseInt(e?.target?.value) || 0)))}
                min="0"
                max="59"
                className="w-20 px-3 py-2.5 border border-border rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-text-secondary">min</span>
            </div>
            {billable && (
              <div className="ml-auto text-sm font-body-semibold text-success-700">
                KES {Number(billableAmount)?.toLocaleString()}
              </div>
            )}
          </div>
          {errors?.duration && <p className="text-xs text-error-500 mt-1">{errors?.duration}</p>}
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e?.target?.value)}
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={e => setStartTime(e?.target?.value)}
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Case */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case Name</label>
            <input
              type="text"
              value={caseName}
              onChange={e => setCaseName(e?.target?.value)}
              placeholder="Enter case name"
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case ID</label>
            <input
              type="text"
              value={caseId}
              onChange={e => setCaseId(e?.target?.value)}
              placeholder="e.g. CASE-2024-001"
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Rate & Billable */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">
              Hourly Rate (KES) <span className="text-error-500">*</span>
            </label>
            <input
              type="number"
              value={hourlyRate}
              onChange={e => setHourlyRate(e?.target?.value)}
              min="0"
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                errors?.hourlyRate ? 'border-error-300' : 'border-border'
              }`}
            />
            {errors?.hourlyRate && <p className="text-xs text-error-500 mt-1">{errors?.hourlyRate}</p>}
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Billable</label>
            <button
              type="button"
              onClick={() => setBillable(!billable)}
              className={`flex items-center gap-2 w-full px-3 py-2.5 border rounded-lg text-sm font-body-medium transition-colors ${
                billable
                  ? 'bg-success-50 text-success-700 border-success-200' :'bg-secondary-50 text-text-secondary border-border'
              }`}
            >
              <Icon name={billable ? 'CheckCircle' : 'Circle'} size={16} />
              {billable ? 'Billable' : 'Non-Billable'}
            </button>
          </div>
        </div>

        {/* Activity Description */}
        <div>
          <label className="block text-sm font-body-medium text-text-secondary mb-1.5">
            Activity Description <span className="text-error-500">*</span>
          </label>
          <textarea
            value={activityDescription}
            onChange={e => setActivityDescription(e?.target?.value)}
            placeholder="Describe the work performed in detail..."
            rows={3}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
              errors?.activityDescription ? 'border-error-300' : 'border-border'
            }`}
          />
          {errors?.activityDescription && <p className="text-xs text-error-500 mt-1">{errors?.activityDescription}</p>}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg font-body-medium hover:bg-primary-700 transition-colors"
          >
            <Icon name="Save" size={16} />
            {editingEntry ? 'Update Entry' : 'Save Entry'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 border border-border rounded-lg text-sm font-body-medium text-text-secondary hover:bg-secondary-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManualTimeEntry;
